import { Component, createRef } from "react";
import { Navigate } from "react-router-dom";
import AsyncSelect from "react-select/async";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Alert, Button, Card, Col, Form, Modal, Row, Table } from "react-bootstrap";
import { FaEdit, FaEraser, FaEye, FaUpload } from "react-icons/fa";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import programasService from "../../api/programas/programasService";
import { estados, getCurrentUser, hasPerm } from "../../common/utils";
import {atualizarEstadoCeaf, baixarModeloPlanilhaCeaf, criarDocumentoEstadoCeaf, criarExameCeaf, criarExameEstadoCeaf, criarTipoDocumentoCeaf, detalharEstadoCeaf, enriquecerEstadoComDocsExames, importarPlanilhaCeaf, listarDocumentosEstadoCeaf, listarEstadosCeaf, listarExamesCeaf, listarExamesEstadoCeaf, listarTiposDocumentoCeaf, removerDocumentoEstadoCeaf, removerExameEstadoCeaf, resolverProdutosCeaf,} from "../../api/ceaf/ceafService";

const EDITOR_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const normalizeText = (value = "") =>
  String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

const parseSemicolonItems = (value = "") =>
  String(value)
    .split(";")
    .map((item) => item.trim())
    .filter(Boolean);

const parseBreaklineItems = (value = "") =>
  String(value)
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);

const parseLinks = (value = "") =>
  parseBreaklineItems(value).filter((item) => item.startsWith("http"));

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const normalizeLines = (lines = []) =>
  lines.map((line) => String(line).trim()).filter(Boolean);

const getHtmlDocument = (html = "") => {
  if (typeof window === "undefined") return null;
  return new window.DOMParser().parseFromString(html || "", "text/html");
};

const extractLinesFromHtml = (html = "") => {
  const doc = getHtmlDocument(html);
  if (!doc) return parseBreaklineItems(html);

  const listItems = normalizeLines(
    Array.from(doc.querySelectorAll("li")).map((item) => item.textContent || "")
  );
  if (listItems.length) return listItems;

  const blockItems = normalizeLines(
    Array.from(doc.body.querySelectorAll("p, div, blockquote, pre, h1, h2, h3, h4, h5, h6"))
      .flatMap((item) => String(item.textContent || "").split("\n"))
  );

  if (blockItems.length) return blockItems;
  return normalizeLines(String(doc.body.textContent || "").split("\n"));
};

const extractLinksFromHtml = (html = "") => {
  const doc = getHtmlDocument(html);
  if (!doc) return parseLinks(html);

  const hrefs = Array.from(doc.querySelectorAll("a"))
    .map((item) => item.getAttribute("href") || "")
    .filter((item) => /^https?:\/\//i.test(item));

  const rawText = doc.body.textContent || "";
  const textUrls = rawText.match(/https?:\/\/[^\s]+/g) || [];
  return Array.from(new Set(normalizeLines([...hrefs, ...textUrls])));
};

const buildEditorValue = (mode, value = "") => {
  if (!value) return "";

  if (mode === "semicolon-list") {
    const items = parseSemicolonItems(value);
    return items.length ? `<ul>${items.map((item) => `<li>${escapeHtml(item)}</li>`).join("")}</ul>` : "";
  }

  if (mode === "breakline-list") {
    const items = parseBreaklineItems(value);
    return items.length ? `<ul>${items.map((item) => `<li>${escapeHtml(item)}</li>`).join("")}</ul>` : "";
  }

  if (mode === "links") {
    const links = parseLinks(value);
    return links.length
      ? links.map((link) => `<p><a href="${escapeHtml(link)}">${escapeHtml(link)}</a></p>`).join("")
      : "";
  }

  return parseBreaklineItems(value)
    .map((line) => `<p>${escapeHtml(line)}</p>`)
    .join("");
};

const serializeEditorValue = (mode, html = "") => {
  if (!html) return "";

  if (mode === "semicolon-list") {
    return Array.from(new Set(extractLinesFromHtml(html).map((item) => item.trim()).filter(Boolean))).join("; ");
  }

  if (mode === "breakline-list") {
    return extractLinesFromHtml(html).join("\n");
  }

  if (mode === "links") {
    return extractLinksFromHtml(html).join("\n");
  }

  return extractLinesFromHtml(html).join("\n");
};

class FluxoCeaf extends Component {
  constructor(props) {
    super(props);

    this.fileInputRef = createRef();
    this.state = {
      registros: [],
      registrosFiltrados: [],
      filtro_programa_id: "",
      filtro_programa_nome: "",
      filtro_estado: "",
      estadosDisponiveis: [],
      exibirModalUpload: false,
      exibirModalDetalhes: false,
      exibirModalEdicao: false,
      upload_programa_id: "",
      upload_programa_nome: "",
      upload_file: null,
      // resolvidos por nome no mount — os ids diferem entre os ambientes
      upload_produto_crysvita_id: "",
      upload_produto_mepsevii_id: "",
      registroSelecionado: null,
      editorSecao: null,
      editorConteudo: "",
      uploading: false,
      salvandoEdicao: false,
    };
  }

  async componentDidMount() {
    const dadosUsuario = getCurrentUser();
    const programaId = dadosUsuario?.ambiente_programa_id || "";

    // resolve antes de carregar os registros: as secoes de exames dependem dos ids
    await this.resolverProdutos();

    if (!programaId) return;

    const programaNome = localStorage.getItem(`programa_nome_${programaId}`) || "";
    this.hydrateRecords({
      filtro_programa_id: programaId,
      filtro_programa_nome: programaNome,
    });
  }

  resolverProdutos = async () => {
    const { crysvitaId, mepseviiId } = await resolverProdutosCeaf().catch(() => ({}));
    return new Promise((resolve) => {
      this.setState(
        {
          upload_produto_crysvita_id: crysvitaId || "",
          upload_produto_mepsevii_id: mepseviiId || "",
        },
        resolve
      );
    });
  };

  getProdutoIds = () => ({
    crysvitaId: Number(this.state.upload_produto_crysvita_id) || undefined,
    mepseviiId: Number(this.state.upload_produto_mepsevii_id) || undefined,
  });

  podeVisualizarFluxoCeaf = () => {
    const dadosUsuario = getCurrentUser();
    return (
      hasPerm("view_ceaf_estado") ||
      hasPerm("view_chamados") ||
      dadosUsuario?.tipo_usuario === "operador"
    );
  };

  podeEditarFluxoCeaf = () => {
    const dadosUsuario = getCurrentUser();
    return hasPerm("change_ceaf_estado") || Boolean(dadosUsuario?.is_superuser);
  };

  hydrateRecords = async (extraState = {}) => {
    const programaId = extraState.filtro_programa_id ?? this.state.filtro_programa_id;
    try {
      const registros = await listarEstadosCeaf(programaId || undefined);
      this.setState(
        {
          registros,
          ...extraState,
        },
        this.applyFilters
      );
    } catch (error) {
      console.error("Erro ao carregar estados CEAF:", error);
      const MySwal = withReactContent(Swal);
      MySwal.fire({ icon: "error", title: "Erro", text: "Não foi possivel carregar os registros da API.", showCloseButton: true });
      this.setState({ registros: [], ...extraState }, this.applyFilters);
    }
  };

  applyFilters = () => {
    const { registros, filtro_programa_id, filtro_estado } = this.state;

    const registrosFiltrados = registros.filter((item) => {
      const matchPrograma = filtro_programa_id
        ? String(item.programa_id ?? item.dados_programa?.id) === String(filtro_programa_id)
        : true;

      const matchEstado = filtro_estado
        ? item.uf === filtro_estado || normalizeText(item.estado_nome) === normalizeText(filtro_estado)
        : true;

      return matchPrograma && matchEstado;
    });

    const estadosDisponiveis = [...new Set(
      registros
        .filter((item) =>
          filtro_programa_id
            ? String(item.programa_id ?? item.dados_programa?.id) === String(filtro_programa_id)
            : true
        )
        .map((item) => item.uf)
        .filter(Boolean)
    )]
      .sort()
      .map((uf) => ({
        value: uf,
        label: `${estados[uf] || uf} (${uf})`,
      }));

    this.setState({
      registrosFiltrados,
      estadosDisponiveis,
    });
  };

  handleChangeFiltros = (event, fieldName) => {
    if (fieldName === "programa_id") {
      const novoId = event?.value || "";
      const novoNome = event?.label || "";

      if (!novoId) {
        this.setState({
          filtro_programa_id: "",
          filtro_programa_nome: "",
          filtro_estado: "",
          registros: [],
          registrosFiltrados: [],
          estadosDisponiveis: [],
        });
        return;
      }

      this.hydrateRecords({
        filtro_programa_id: novoId,
        filtro_programa_nome: novoNome,
        filtro_estado: "",
      });
      return;
    }

    this.setState({ [fieldName]: event.target.value }, this.applyFilters);
  };

  handleChangeUpload = (event, fieldName) => {
    if (fieldName === "programa_id") {
      this.setState({
        upload_programa_id: event?.value || "",
        upload_programa_nome: event?.label || "",
      });
      return;
    }

    if (fieldName === "arquivo") {
      this.setState({ upload_file: event.target.files?.[0] || null });
    }
  };

  abrirModalUpload = () => {
    const dadosUsuario = getCurrentUser();
    const programaId = this.state.filtro_programa_id || dadosUsuario?.ambiente_programa_id || "";
    const programaNome =
      this.state.filtro_programa_nome ||
      localStorage.getItem(`programa_nome_${programaId}`) ||
      "";

    this.setState({
      exibirModalUpload: true,
      upload_programa_id: programaId,
      upload_programa_nome: programaNome,
      upload_file: null,
    });
  };

  fecharModalUpload = () => {
    this.setState({
      exibirModalUpload: false,
      upload_file: null,
    });

    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = "";
    }
  };

  carregarRegistroSelecionado = async (registroId, fallbackRegistro = null) => {
    const baseRegistro = await detalharEstadoCeaf(registroId).catch(() => fallbackRegistro);
    if (!baseRegistro) return null;

    const enriquecido = await enriquecerEstadoComDocsExames(baseRegistro, this.getProdutoIds()).catch(() => baseRegistro);
    this.setState((prev) => {
      if (!prev.registroSelecionado || prev.registroSelecionado.id !== registroId) {
        return { registroSelecionado: enriquecido };
      }
      return { registroSelecionado: { ...prev.registroSelecionado, ...enriquecido } };
    });
    return enriquecido;
  };

  abrirDetalhes = async (registro) => {
    this.setState({ registroSelecionado: registro, exibirModalDetalhes: true });
    try {
      await this.carregarRegistroSelecionado(registro.id, registro);
    } catch {
      // mantem a visualizacao com os dados ja disponiveis
    }
  };

  fecharDetalhes = () => {
    this.setState({
      registroSelecionado: null,
      exibirModalDetalhes: false,
      exibirModalEdicao: false,
      editorSecao: null,
      editorConteudo: "",
    });
  };

  limparFiltros = () => {
    const dadosUsuario = getCurrentUser();
    const programaId = dadosUsuario?.ambiente_programa_id || "";
    const programaNome = programaId
      ? localStorage.getItem(`programa_nome_${programaId}`) || ""
      : "";

    if (!programaId) {
      this.setState({
        filtro_programa_id: "",
        filtro_programa_nome: "",
        filtro_estado: "",
        registros: [],
        registrosFiltrados: [],
        estadosDisponiveis: [],
      });
      return;
    }

    this.setState(
      {
        filtro_programa_id: programaId,
        filtro_programa_nome: programaNome,
        filtro_estado: "",
      },
      this.applyFilters
    );
  };

  importarPlanilha = async () => {
    const {
      upload_programa_id,
      upload_programa_nome,
      upload_file,
      upload_produto_crysvita_id,
      upload_produto_mepsevii_id,
    } = this.state;

    const MySwal = withReactContent(Swal);

    if (!this.podeEditarFluxoCeaf()) {
      MySwal.fire({ icon: "warning", title: "Acesso restrito", text: "Seu perfil nao permite importar planilhas CEAF.", showCloseButton: true });
      return;
    }

    if (!upload_programa_id || !upload_programa_nome) {
      MySwal.fire({ icon: "warning", title: "Atenção", text: "Selecione o programa que receberá a planilha.", showCloseButton: true });
      return;
    }

    if (!upload_file) {
      MySwal.fire({ icon: "warning", title: "Atencao", text: "Selecione um arquivo Excel para continuar.", showCloseButton: true });
      return;
    }

    // sem os produtos os estados seriam importados sem os exames, sem nenhum aviso
    if (!upload_produto_crysvita_id || !upload_produto_mepsevii_id) {
      MySwal.fire({
        icon: "warning",
        title: "Produtos não identificados",
        text: "Não foi possível identificar os produtos Crysvita e Mepsevii. A importação gravaria os estados sem os exames obrigatórios — verifique se os produtos estão cadastrados e se o seu perfil tem permissão para consultá-los.",
        showCloseButton: true,
      });
      return;
    }

    this.setState({ uploading: true });

    try {
      const resultado = await importarPlanilhaCeaf(
        upload_file,
        upload_programa_id,
        upload_produto_crysvita_id || undefined,
        upload_produto_mepsevii_id || undefined,
      );

      await this.hydrateRecords({
        filtro_programa_id: upload_programa_id,
        filtro_programa_nome: upload_programa_nome,
        filtro_estado: "",
      });

      this.fecharModalUpload();

      const erroMsg = resultado.erros > 0
        ? `<br/><small class="text-warning">${resultado.erros} linha(s) com erro ignoradas.</small>`
        : "";

      MySwal.fire({
        icon: "success",
        title: "Importação concluída!",
        html: `${resultado.estados} estado(s), ${resultado.docs_criados} doc(s), ${resultado.exames_criados} exame(s) importados.${erroMsg}`,
        showCloseButton: true,
      });
    } catch (error) {
      console.error("Erro ao importar planilha CEAF:", error);
      MySwal.fire({
        icon: "error",
        title: "Erro na importação",
        text: error?.response?.data?.detail || error?.message || "Não foi possível importar a planilha.",
        showCloseButton: true,
      });
    } finally {
      this.setState({ uploading: false });
    }
  };

  baixarModeloPlanilha = async () => {
    const MySwal = withReactContent(Swal);
    try {
      const blob = await baixarModeloPlanilhaCeaf();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Fluxo_CEAF_Executivo.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao baixar modelo CEAF:", error);
      MySwal.fire({
        icon: "error",
        title: "Erro no download",
        text: error?.response?.data?.detail || "Não foi possível baixar a planilha modelo.",
        showCloseButton: true,
      });
    }
  };

  abrirEditor = (config) => {
    this.setState({
      exibirModalEdicao: true,
      editorSecao: config,
      editorConteudo: buildEditorValue(config.mode, config.value || ""),
    });
  };

  fecharEditor = () => {
    if (this.state.salvandoEdicao) return;

    this.setState({
      exibirModalEdicao: false,
      editorSecao: null,
      editorConteudo: "",
    });
  };

  garantirTipoDocumento = async (nome, categoria) => {
    const existentes = await listarTiposDocumentoCeaf(categoria, nome);
    const encontrado = existentes.find(
      (item) => item.categoria === categoria && normalizeText(item.nome) === normalizeText(nome)
    );

    if (encontrado) return encontrado;
    return criarTipoDocumentoCeaf({ nome, categoria, descricao: "" });
  };

  garantirExame = async (nome) => {
    const existentes = await listarExamesCeaf(nome);
    const encontrado = existentes.find((item) => normalizeText(item.nome) === normalizeText(nome));

    if (encontrado) return encontrado;
    return criarExameCeaf({ nome, descricao: "" });
  };

  sincronizarDocumentos = async (categoria, conteudoHtml) => {
    const { registroSelecionado } = this.state;
    const desejados = Array.from(new Set(parseSemicolonItems(serializeEditorValue("semicolon-list", conteudoHtml))));
    const atuais = await listarDocumentosEstadoCeaf(registroSelecionado.id, categoria);

    const mapaDesejados = new Map(desejados.map((item) => [normalizeText(item), item]));
    const mapaAtuais = new Map(
      atuais
        .map((item) => {
          const nome = item.dados_documento?.nome || item.documento_tipo?.nome || "";
          return [normalizeText(nome), item];
        })
        .filter(([key]) => key)
    );

    for (const item of atuais) {
      const nome = item.dados_documento?.nome || item.documento_tipo?.nome || "";
      if (!mapaDesejados.has(normalizeText(nome))) {
        await removerDocumentoEstadoCeaf(item.id);
      }
    }

    for (const [key, nome] of mapaDesejados.entries()) {
      if (mapaAtuais.has(key)) continue;
      const tipo = await this.garantirTipoDocumento(nome, categoria);
      await criarDocumentoEstadoCeaf({
        ceaf_estado_id: registroSelecionado.id,
        documento_tipo_id: tipo.id,
        obrigatorio: true,
        observacao: "",
      });
    }
  };

  sincronizarExames = async (produtoId, conteudoHtml) => {
    const { registroSelecionado } = this.state;
    if (!produtoId) {
      throw new Error("Configure o ID do produto antes de editar esta seção.");
    }

    const desejados = Array.from(new Set(parseSemicolonItems(serializeEditorValue("semicolon-list", conteudoHtml))));
    const atuais = await listarExamesEstadoCeaf(registroSelecionado.id, produtoId);

    const mapaDesejados = new Map(desejados.map((item) => [normalizeText(item), item]));
    const mapaAtuais = new Map(
      atuais
        .map((item) => {
          const nome = item.dados_exame?.nome || item.exame?.nome || "";
          return [normalizeText(nome), item];
        })
        .filter(([key]) => key)
    );

    for (const item of atuais) {
      const nome = item.dados_exame?.nome || item.exame?.nome || "";
      if (!mapaDesejados.has(normalizeText(nome))) {
        await removerExameEstadoCeaf(item.id);
      }
    }

    for (const [key, nome] of mapaDesejados.entries()) {
      if (mapaAtuais.has(key)) continue;
      const exame = await this.garantirExame(nome);
      await criarExameEstadoCeaf({
        ceaf_estado_id: registroSelecionado.id,
        produto_id: produtoId,
        exame_id: exame.id,
        obrigatorio: true,
        observacao: "",
      });
    }
  };

  atualizarListasLocais = async (registroId) => {
    const filtrosAtuais = {
      filtro_programa_id: this.state.filtro_programa_id,
      filtro_programa_nome: this.state.filtro_programa_nome,
      filtro_estado: this.state.filtro_estado,
    };

    await this.hydrateRecords(filtrosAtuais);
    await this.carregarRegistroSelecionado(registroId, this.state.registroSelecionado);
  };

  salvarEdicao = async () => {
    const { editorSecao, editorConteudo, registroSelecionado } = this.state;
    if (!editorSecao || !registroSelecionado) return;

    const MySwal = withReactContent(Swal);
    this.setState({ salvandoEdicao: true });

    try {
      if (editorSecao.persistType === "estado") {
        await atualizarEstadoCeaf(registroSelecionado.id, {
          [editorSecao.field]: serializeEditorValue(editorSecao.mode, editorConteudo),
        });
      }

      if (editorSecao.persistType === "documentos") {
        await this.sincronizarDocumentos(editorSecao.categoria, editorConteudo);
      }

      if (editorSecao.persistType === "exames") {
        await this.sincronizarExames(editorSecao.produtoId, editorConteudo);
      }

      await this.atualizarListasLocais(registroSelecionado.id);
      this.fecharEditor();
      MySwal.fire({
        icon: "success",
        title: "Alterações salvas",
        text: `Os dados de ${editorSecao.title} foram atualizados com sucesso.`,
        showCloseButton: true,
      });
    } catch (error) {
      console.error("Erro ao salvar edição CEAF:", error);
      MySwal.fire({
        icon: "error",
        title: "Não foi possível salvar",
        text: error?.response?.data?.detail || error?.message || "Tivemos um problema ao salvar esta seção.",
        showCloseButton: true,
      });
    } finally {
      this.setState({ salvandoEdicao: false });
    }
  };

  renderCardHeader = (title, editConfig) => (
    <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
      <h6 className="fw-bold mb-0">{title}</h6>
      {editConfig ? (
        <Button
          variant="light"
          size="sm"
          className="border"
          onClick={() => this.abrirEditor(editConfig)}
          title={`Editar ${title}`}
          style={{ width: 32, height: 32, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
        >
          <FaEdit />
        </Button>
      ) : null}
    </div>
  );

  renderListSection = (title, value, parser = parseBreaklineItems, editConfig = null) => {
    const items = parser(value);

    return (
      <Card className="h-100 border-0 shadow-sm">
        <Card.Body>
          {this.renderCardHeader(title, editConfig)}
          {items.length ? (
            <ul className="mb-0 ps-3">
              {items.map((item, index) => (
                <li key={`${title}-${index}`} className="mb-2">
                  {item}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted mb-0">Não informado.</p>
          )}
        </Card.Body>
      </Card>
    );
  };

  renderTextSection = (title, value, editConfig = null) => (
    <Card className="h-100 border-0 shadow-sm">
      <Card.Body>
        {this.renderCardHeader(title, editConfig)}
        <p className="mb-0 text-break" style={{ whiteSpace: "pre-line" }}>
          {value || "Não informado."}
        </p>
      </Card.Body>
    </Card>
  );

  renderLinksSection = (title, value, editConfig = null) => {
    const links = parseLinks(value);

    return (
      <Card className="h-100 border-0 shadow-sm">
        <Card.Body>
          {this.renderCardHeader(title, editConfig)}
          {links.length ? (
            <div className="d-flex flex-column gap-2">
              {links.map((link) => (
                <a key={link} href={link} target="_blank" rel="noreferrer">
                  {link}
                </a>
              ))}
            </div>
          ) : (
            <p className="text-muted mb-0">Não informado.</p>
          )}
        </Card.Body>
      </Card>
    );
  };

  render() {
    if (!this.podeVisualizarFluxoCeaf()) {
      return <Navigate to="/sem-permissao" />;
    }

    const dadosUsuario = getCurrentUser();
    const bloqueiaPrograma = Boolean(dadosUsuario?.ambiente_programa_id);
    const podeEditar = this.podeEditarFluxoCeaf();
    const {
      registrosFiltrados,
      filtro_programa_id,
      filtro_programa_nome,
      filtro_estado,
      estadosDisponiveis,
      exibirModalUpload,
      exibirModalDetalhes,
      exibirModalEdicao,
      upload_programa_id,
      upload_programa_nome,
      upload_produto_crysvita_id,
      upload_produto_mepsevii_id,
      registroSelecionado,
      editorSecao,
      editorConteudo,
      uploading,
      salvandoEdicao,
    } = this.state;

    const crysvitaId = Number(upload_produto_crysvita_id) || undefined;
    const mepseviiId = Number(upload_produto_mepsevii_id) || undefined;

    return (
      <div>
        <Row className="align-items-center">
          <Col sm={6} lg={6}>
            <h4 className="page-title">Fluxo CEAF - Executivo</h4>
          </Col>
          <Col sm={6} lg={6} className="text-end">
            {podeEditar && (
              <Button variant="success" className="mb-3" size="sm" onClick={this.abrirModalUpload}>
                <FaUpload className="me-2" /> Enviar Planilha
              </Button>
            )}
          </Col>
        </Row>

        <Card className="cardDestaque">
          <Card.Body>
            <h4>Filtros</h4>
            <Form>
              <Row className="align-items-end">
                <Col sm={12} md={5}>
                  <Form.Group className="mb-3">
                    <Form.Label>Programa</Form.Label>
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      isDisabled={bloqueiaPrograma}
                      loadOptions={programasService.loadProgramas}
                      placeholder="Todos"
                      noOptionsMessage={() => "Nenhum programa encontrado"}
                      onChange={(event) => this.handleChangeFiltros(event, "programa_id")}
                      value={
                        filtro_programa_id
                          ? { value: filtro_programa_id, label: filtro_programa_nome || "Programa selecionado" }
                          : null
                      }
                    />
                  </Form.Group>
                </Col>

                <Col sm={12} md={5}>
                  <Form.Group className="mb-3">
                    <Form.Label>Estado</Form.Label>
                    <Form.Select
                      value={filtro_estado}
                      onChange={(event) => this.handleChangeFiltros(event, "filtro_estado")}
                    >
                      <option value="">Todos</option>
                      {estadosDisponiveis.map((estadoOption) => (
                        <option key={estadoOption.value} value={estadoOption.value}>
                          {estadoOption.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Button type="button" variant="light" size="sm" onClick={this.limparFiltros}>
                    <FaEraser /> Limpar filtro
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>

        <p className="mt-4 mb-3 text-muted">{registrosFiltrados.length} registro(s) encontrado(s)</p>

        <Card className="cardDestaque">
          <Card.Body className="pt-0">
            {!filtro_programa_id && !bloqueiaPrograma ? (
              <Alert variant="info" className="mt-3 mb-0">
                Selecione um programa acima para visualizar os dados do Fluxo CEAF.
              </Alert>
            ) : registrosFiltrados.length === 0 ? (
              <Alert variant="light" className="mt-3 mb-0">
                Nenhum registro encontrado. Importe a planilha base para começar.
              </Alert>
            ) : (
              <Table responsive className="custom-table">
                <thead>
                  <tr>
                    <th>Estado</th>
                    <th>Programa</th>
                    <th>1º Fornecimento</th>
                    <th>Renovação</th>
                    <th>Modelo Dispensação</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {registrosFiltrados.map((item) => (
                    <tr key={item.id}>
                      <td>{item.estado_nome} ({item.uf})</td>
                      <td>{item.dados_programa?.nome || "-"}</td>
                      <td>{item.primeiro_fornecimento_detalhe || item.primeiro_fornecimento_display || "-"}</td>
                      <td>{item.renovacao_detalhe || item.renovacao_display || "-"}</td>
                      <td>{item.modelo_dispensacao_detalhe || item.modelo_dispensacao_display || "-"}</td>
                      <td className="text-end">
                        <Button variant="light" size="sm" onClick={() => this.abrirDetalhes(item)}>
                          <FaEye className="me-2" /> Visualizar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        <Modal show={exibirModalUpload} onHide={this.fecharModalUpload} centered>
          <Modal.Header closeButton>
            <Modal.Title>Enviar Planilha CEAF</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Programa</Form.Label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                isDisabled={bloqueiaPrograma}
                loadOptions={programasService.loadProgramas}
                placeholder="Selecione o programa"
                noOptionsMessage={() => "Nenhum programa encontrado"}
                onChange={(event) => this.handleChangeUpload(event, "programa_id")}
                value={
                  upload_programa_id
                    ? { value: upload_programa_id, label: upload_programa_nome || "Programa selecionado" }
                    : null
                }
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Arquivo Excel</Form.Label>
              <div className="mb-2">
                <Button variant="link" className="p-0" onClick={this.baixarModeloPlanilha}>
                  Baixar planilha exemplo
                </Button>
              </div>
              <Form.Control
                ref={this.fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(event) => this.handleChangeUpload(event, "arquivo")}
              />
              <Form.Text className="text-muted">
                A importação salva os dados na base e atualiza os registros do programa selecionado.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={this.fecharModalUpload} disabled={uploading}>
              Cancelar
            </Button>
            <Button variant="success" onClick={this.importarPlanilha} disabled={uploading}>
              <FaUpload className="me-2" />
              {uploading ? "Importando..." : "Importar planilha"}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={exibirModalDetalhes} onHide={this.fecharDetalhes} size="xl" centered scrollable>
          <Modal.Header closeButton>
            <Modal.Title>
              {registroSelecionado ? `${registroSelecionado.estado_nome} (${registroSelecionado.uf})` : "Detalhes do estado"}
              {registroSelecionado?.dados_programa?.nome ? ` - ${registroSelecionado.dados_programa.nome}` : ""}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {registroSelecionado && (
              <Row className="g-3">
                <Col md={6}>
                  {this.renderTextSection(
                    "1º Fornecimento",
                    registroSelecionado.primeiro_fornecimento_detalhe || registroSelecionado.primeiro_fornecimento_display,
                    podeEditar ? {
                      title: "1º Fornecimento",
                      mode: "text",
                      persistType: "estado",
                      field: "primeiro_fornecimento_detalhe",
                      value: registroSelecionado.primeiro_fornecimento_detalhe || registroSelecionado.primeiro_fornecimento_display || "",
                    } : null
                  )}
                </Col>
                <Col md={6}>
                  {this.renderTextSection(
                    "Renovação",
                    registroSelecionado.renovacao_detalhe || registroSelecionado.renovacao_display,
                    podeEditar ? {
                      title: "Renovação",
                      mode: "text",
                      persistType: "estado",
                      field: "renovacao_detalhe",
                      value: registroSelecionado.renovacao_detalhe || registroSelecionado.renovacao_display || "",
                    } : null
                  )}
                </Col>
                <Col md={6}>
                  {this.renderListSection(
                    "Documentação do Paciente",
                    registroSelecionado.docPaciente ?? "",
                    parseSemicolonItems,
                    podeEditar ? {
                      title: "Documentação do Paciente",
                      mode: "semicolon-list",
                      persistType: "documentos",
                      categoria: "paciente",
                      value: registroSelecionado.docPaciente ?? "",
                    } : null
                  )}
                </Col>
                <Col md={6}>
                  {this.renderListSection(
                    "Documentação Médica",
                    registroSelecionado.docMedico ?? "",
                    parseSemicolonItems,
                    podeEditar ? {
                      title: "Documentação Médica",
                      mode: "semicolon-list",
                      persistType: "documentos",
                      categoria: "medico",
                      value: registroSelecionado.docMedico ?? "",
                    } : null
                  )}
                </Col>
                <Col md={6}>
                  {this.renderListSection(
                    "Exames Crysvita",
                    registroSelecionado.examesCrysvita ?? "",
                    parseSemicolonItems,
                    podeEditar ? {
                      title: "Exames Crysvita",
                      mode: "semicolon-list",
                      persistType: "exames",
                      produtoId: crysvitaId,
                      value: registroSelecionado.examesCrysvita ?? "",
                    } : null
                  )}
                </Col>
                <Col md={6}>
                  {this.renderListSection(
                    "Exames Mepsevii",
                    registroSelecionado.examesMepsevii ?? "",
                    parseSemicolonItems,
                    podeEditar ? {
                      title: "Exames Mepsevii",
                      mode: "semicolon-list",
                      persistType: "exames",
                      produtoId: mepseviiId,
                      value: registroSelecionado.examesMepsevii ?? "",
                    } : null
                  )}
                </Col>
                <Col md={6}>
                  {this.renderListSection(
                    "Exames Crysvita - Renovação",
                    registroSelecionado.exames_crysvita_renovacao ?? "",
                    parseSemicolonItems,
                    podeEditar ? {
                      title: "Exames Crysvita - Renovação",
                      mode: "semicolon-list",
                      persistType: "estado",
                      field: "exames_crysvita_renovacao",
                      value: registroSelecionado.exames_crysvita_renovacao ?? "",
                    } : null
                  )}
                </Col>
                <Col md={6}>
                  {this.renderListSection(
                    "Exames Mepsevii - Renovação",
                    registroSelecionado.exames_mepsevii_renovacao ?? "",
                    parseSemicolonItems,
                    podeEditar ? {
                      title: "Exames Mepsevii - Renovação",
                      mode: "semicolon-list",
                      persistType: "estado",
                      field: "exames_mepsevii_renovacao",
                      value: registroSelecionado.exames_mepsevii_renovacao ?? "",
                    } : null
                  )}
                </Col>
                <Col md={6}>
                  {this.renderTextSection(
                    "Modelo de Dispensação",
                    registroSelecionado.modelo_dispensacao_detalhe || registroSelecionado.modelo_dispensacao_display,
                    podeEditar ? {
                      title: "Modelo de Dispensação",
                      mode: "text",
                      persistType: "estado",
                      field: "modelo_dispensacao_detalhe",
                      value: registroSelecionado.modelo_dispensacao_detalhe || registroSelecionado.modelo_dispensacao_display || "",
                    } : null
                  )}
                </Col>
                <Col md={6}>
                  {this.renderTextSection(
                    "Cadastro e Fornecimento",
                    registroSelecionado.cadastro_fornecimento,
                    podeEditar ? {
                      title: "Cadastro e Fornecimento",
                      mode: "text",
                      persistType: "estado",
                      field: "cadastro_fornecimento",
                      value: registroSelecionado.cadastro_fornecimento || "",
                    } : null
                  )}
                </Col>
                <Col md={6}>
                  {this.renderListSection(
                    "Locais de Dispensação / Unidades",
                    registroSelecionado.locais_dispensacao,
                    parseBreaklineItems,
                    podeEditar ? {
                      title: "Locais de Dispensação / Unidades",
                      mode: "breakline-list",
                      persistType: "estado",
                      field: "locais_dispensacao",
                      value: registroSelecionado.locais_dispensacao || "",
                    } : null
                  )}
                </Col>
                <Col md={6}>
                  {this.renderTextSection(
                    "Observação",
                    registroSelecionado.observacao,
                    podeEditar ? {
                      title: "Observação",
                      mode: "text",
                      persistType: "estado",
                      field: "observacao",
                      value: registroSelecionado.observacao || "",
                    } : null
                  )}
                </Col>
                <Col md={12}>
                  {this.renderLinksSection(
                    "Links Oficiais",
                    registroSelecionado.link_oficial,
                    podeEditar ? {
                      title: "Links Oficiais",
                      mode: "links",
                      persistType: "estado",
                      field: "link_oficial",
                      value: registroSelecionado.link_oficial || "",
                    } : null
                  )}
                </Col>
              </Row>
            )}
          </Modal.Body>
        </Modal>

        <Modal show={exibirModalEdicao} onHide={this.fecharEditor} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>{editorSecao ? `Editar ${editorSecao.title}` : "Editar seção"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3 text-muted" style={{ fontSize: 14 }}>
              {editorSecao?.mode === "semicolon-list" && "Use linhas ou listas para organizar os itens. Ao salvar, cada item será sincronizado com o estado selecionado."}
              {editorSecao?.mode === "breakline-list" && "Use uma linha por unidade ou local de dispensação."}
              {editorSecao?.mode === "links" && "Adicione um link por linha. Os links validos serão salvos no estado."}
              {!editorSecao || editorSecao?.mode === "text" ? "Edite o conteúdo desta seção e salve para atualizar o estado selecionado." : null}
            </div>
            <ReactQuill
              theme="snow"
              value={editorConteudo}
              onChange={(value) => this.setState({ editorConteudo: value })}
              modules={EDITOR_MODULES}
              style={{ minHeight: 240, marginBottom: 48 }}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={this.fecharEditor} disabled={salvandoEdicao}>
              Cancelar
            </Button>
            <Button variant="success" onClick={this.salvarEdicao} disabled={salvandoEdicao}>
              {salvandoEdicao ? "Salvando..." : "Salvar alterações"}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}

export default FluxoCeaf;
